import { useMemo, useState, type ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  Cancel01Icon,
  LinkSquare02Icon,
  RefreshIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Loader } from "shared/components/Loader";
import type {
  UnderwritingAgentStatus,
  UnderwritingFinding,
  UnderwritingReportKey
} from "api/underwriting-agents/types";
import { IconAI } from "shared/components/ai/IconAI";
import { getFullName } from "shared/utils/get-full-name";
import type { Applicant } from "types/application";
import { cx } from "shared/utils";
import { UNDERWRITING_REPORTS, getReportLabel } from "../rules";
import { FindingCard, NeedsReviewBadge, ReviewedBadge } from "./FindingCard";
import { getReportReviewState, type ReportReviewState } from "./reviewStatus";
import {
  useRerunUnderwritingCheck,
  useReviewUnderwritingFindings,
  useUnderwritingFindings
} from "./useUnderwritingFindings";
import { useRerunRequests, wasRerunRequested } from "./rerunRequests";

type ReviewApplicant = Pick<
  Applicant,
  "id" | "first_name" | "middle_name" | "last_name"
>;

export type UnderwritingAgentReviewTarget = {
  applicantId?: number;
  reportKey?: UnderwritingReportKey;
};

/** Findings to review and in total, for an applicant or one of their reports. */
const countFindings = (
  status: UnderwritingAgentStatus,
  applicantId: number,
  reportKey?: UnderwritingReportKey
) =>
  status.reports
    .filter(
      (report) =>
        report.applicant_id === applicantId &&
        (!reportKey || report.report_key === reportKey)
    )
    .reduce(
      (counts, report) => ({
        toReview: counts.toReview + report.to_review_count,
        total: counts.total + report.to_review_count + report.reviewed_count
      }),
      { toReview: 0, total: 0 }
    );

const pickApplicantId = (
  status: UnderwritingAgentStatus,
  applicants: ReviewApplicant[],
  target: UnderwritingAgentReviewTarget
) =>
  target.applicantId ??
  applicants.find(
    (applicant) =>
      countFindings(status, applicant.id).toReview > 0 ||
      status.reports.some(
        (report) =>
          report.applicant_id === applicant.id && report.status === "failed"
      )
  )?.id ??
  applicants.find((applicant) => countFindings(status, applicant.id).total > 0)
    ?.id ??
  applicants[0]?.id;

const pickReportKey = (
  status: UnderwritingAgentStatus,
  applicantId: number
): UnderwritingReportKey =>
  UNDERWRITING_REPORTS.find((report) =>
    ["to-review", "failed"].includes(
      getReportReviewState(status, applicantId, report.key).state
    )
  )?.key ??
  UNDERWRITING_REPORTS.find(
    (report) => countFindings(status, applicantId, report.key).total > 0
  )?.key ??
  UNDERWRITING_REPORTS[0].key;

const ReportReviewStatus = ({
  state,
  toReview
}: {
  state: ReportReviewState;
  toReview: number;
}) => {
  if (state === "running")
    return (
      <span className="flex shrink-0 items-center gap-1.5 text-sm text-content-400">
        <Loader size={14} />
        Reviewing
      </span>
    );
  if (state === "failed")
    return (
      <span className="shrink-0 text-sm font-medium text-danger-600">
        Error
      </span>
    );
  if (state === "to-review")
    return <NeedsReviewBadge label={`${toReview} to review`} />;
  if (state === "reviewed") return <ReviewedBadge label="Reviewed" />;

  return <span className="shrink-0 text-sm text-content-400">No findings</span>;
};

const ReportRow = ({
  label,
  review,
  selected,
  onClick
}: {
  label: string;
  review: { state: ReportReviewState; toReview: number };
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left transition-colors",
      selected ? "bg-primary-20" : "hover:bg-background-900"
    )}
  >
    <span
      className={cx(
        "truncate text-base font-medium",
        selected ? "text-primary-600" : "text-content-1000"
      )}
    >
      {label}
    </span>
    <ReportReviewStatus state={review.state} toReview={review.toReview} />
  </button>
);

/** A report the agent couldn't review, with Try again; once rerun it reads "Reviewing … again" until the agent is done. */
const ReviewIssueCard = ({
  state,
  rerun,
  reportLabel,
  onRerun
}: {
  state: ReportReviewState;
  rerun: { requested: boolean; pending: boolean };
  reportLabel: string;
  onRerun: () => void;
}) => {
  if (state === "running" && rerun.requested)
    return (
      <div className="flex w-full items-start gap-3 rounded-[10px] border border-alpha-100 bg-white px-5 py-4">
        <Loader size={20} className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-content-1000">
            Reviewing {reportLabel} again
          </p>
          <p className="mt-0.5 text-sm text-content-400">
            Its findings will show up here when the agent is done.
          </p>
        </div>
      </div>
    );
  if (state !== "failed") return null;

  return (
    <div className="flex w-full items-start gap-3 rounded-[10px] border border-danger-200 bg-danger-50 px-5 py-4">
      <HugeiconsIcon
        icon={AlertCircleIcon}
        size={20}
        className="mt-0.5 shrink-0 text-danger-500"
      />
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold text-danger-700">
          The agent couldn&apos;t review {reportLabel}
        </p>
        <p className="mt-0.5 text-sm text-danger-700">
          Review this report yourself or try again.
        </p>
      </div>
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        HugeIcon={RefreshIcon}
        loading={rerun.pending}
        onClick={onRerun}
      >
        Try again
      </Button>
    </div>
  );
};

/** What the pane says when a report has no findings to list: still running, failed (its error card says so), or clean. */
const NoFindings = ({
  state,
  rerunRequested
}: {
  state: ReportReviewState;
  rerunRequested: boolean;
}) => {
  // A failed or rerun report already says so in its own card above.
  if (state === "failed" || (state === "running" && rerunRequested))
    return null;
  if (state === "running")
    return (
      <div className="flex items-center gap-2">
        <Loader size={16} className="shrink-0" />
        <p className="text-sm text-content-400">
          The agent is still reviewing this report. Its findings will show up
          here when it&apos;s done.
        </p>
      </div>
    );

  return (
    <p className="text-base text-content-400">
      The agent found nothing to flag in this report.
    </p>
  );
};

/** The selected report's findings, or why there are none to list. */
const ReportFindings = ({
  findings,
  loading,
  failed,
  state,
  rerunRequested,
  renderFinding
}: {
  findings: UnderwritingFinding[];
  loading: boolean;
  failed: boolean;
  state: ReportReviewState;
  rerunRequested: boolean;
  renderFinding: (finding: UnderwritingFinding) => ReactNode;
}) => {
  if (loading)
    return (
      <div className="flex justify-center py-10">
        <Loader size={32} />
      </div>
    );
  if (failed)
    return (
      <p className="text-sm text-danger-700">
        Couldn&apos;t load the agent&apos;s findings. Close this and try again
        in a moment.
      </p>
    );
  if (!findings.length)
    return <NoFindings state={state} rerunRequested={rerunRequested} />;

  return (
    <>
      {findings.map((finding) => (
        <article
          key={finding.id}
          className="w-full rounded-[10px] border border-alpha-100 bg-white px-5 py-4"
        >
          {renderFinding(finding)}
        </article>
      ))}
    </>
  );
};

/**
 * The underwriting agent's findings across the application, per applicant and
 * screening report. Mount it when it opens, so it starts on `target`.
 */
export const UnderwritingAgentReviewModal = ({
  applicationId,
  applicants,
  status,
  target,
  onClose,
  onViewReport
}: {
  applicationId: string;
  applicants: ReviewApplicant[];
  status: UnderwritingAgentStatus;
  target: UnderwritingAgentReviewTarget;
  onClose: () => void;
  onViewReport?: (
    applicantId: number,
    reportKey: UnderwritingReportKey
  ) => void;
}) => {
  const [applicantId, setApplicantId] = useState(() =>
    pickApplicantId(status, applicants, target)
  );
  const [reportKey, setReportKey] = useState<UnderwritingReportKey>(
    () => target.reportKey ?? pickReportKey(status, applicantId)
  );

  const {
    data: allFindings,
    isPending: findingsLoading,
    isError: findingsFailed
  } = useUnderwritingFindings(applicationId, { enabled: true });
  const { reviewOne, undoOne, reviewAll, isPending } =
    useReviewUnderwritingFindings(applicationId);
  const rerun = useRerunUnderwritingCheck(applicationId);
  const rerunRequests = useRerunRequests();

  const findings = useMemo(
    () =>
      (allFindings ?? []).filter(
        (finding) =>
          finding.applicant_id === applicantId &&
          finding.report_key === reportKey
      ),
    [allFindings, applicantId, reportKey]
  );

  const applicantTabs = useMemo<Tab<string>[]>(
    () =>
      applicants.map((applicant) => ({
        value: String(applicant.id),
        label: getFullName({
          firstName: applicant.first_name,
          middleName: applicant.middle_name,
          lastName: applicant.last_name,
          short: true
        })
      })),
    [applicants]
  );

  const applicantToReviewCount = countFindings(status, applicantId).toReview;
  const reportState = getReportReviewState(
    status,
    applicantId,
    reportKey
  ).state;
  const reportLabel = getReportLabel(reportKey);
  const rerunRequested = wasRerunRequested(
    rerunRequests,
    applicationId,
    applicantId,
    reportKey
  );
  const rerunPending =
    rerun.isPending &&
    rerun.variables?.applicantId === applicantId &&
    rerun.variables?.reportKey === reportKey;
  const hasApplicantTabs = applicants.length > 1;
  const selectedApplicantName = applicantTabs.find(
    (tab) => tab.value === String(applicantId)
  )?.label;

  return (
    <Modal
      opened
      onClose={onClose}
      panelClassName="flex h-[780px] max-h-[calc(100vh-48px)] w-[1120px] max-w-[calc(100vw-48px)] flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_16px_48px_0px_rgba(0,0,0,0.2)]"
    >
      <div
        className={cx(
          "px-6",
          hasApplicantTabs ? "pb-3 pt-5" : "border-b border-alpha-100 py-5"
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex shrink-0 rounded-full shadow-[0px_4px_11.3px_0px_rgba(251,60,160,0.26)]">
              <IconAI bgSize={24} />
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold leading-7 text-content-1000">
                Underwriting agent
              </h2>
              <Show if={!hasApplicantTabs}>
                <p className="mt-0.5 text-sm text-content-400">
                  {selectedApplicantName}
                </p>
              </Show>
            </div>
          </div>
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            className="stroke-content-1000"
            onClick={onClose}
            aria-label="Close"
          />
        </div>
      </div>
      {/* One tab per applicant, in their own row under the header. */}
      <Show if={hasApplicantTabs}>
        <div className="shrink-0 border-b border-alpha-100 px-6">
          <Tabs
            tabs={applicantTabs}
            active={String(applicantId)}
            onChange={(value) => {
              const nextApplicantId = Number(value);
              setApplicantId(nextApplicantId);
              setReportKey(pickReportKey(status, nextApplicantId));
            }}
            tabStyle="line"
            size="sm"
            fullWidthTabs={false}
          />
        </div>
      </Show>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="w-full max-w-[320px] shrink-0 space-y-1 overflow-y-auto border-r border-alpha-100 p-4">
          <p className="px-3 pb-2 pt-1 text-sm font-medium text-content-400">
            Screening reports
          </p>
          {UNDERWRITING_REPORTS.map((report) => (
            <ReportRow
              key={report.key}
              label={report.label}
              review={getReportReviewState(status, applicantId, report.key)}
              selected={report.key === reportKey}
              onClick={() => setReportKey(report.key)}
            />
          ))}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto bg-background-900 p-6">
          <div className="flex items-center justify-between gap-3">
            <p className="text-base font-bold text-content-1000">
              {reportLabel}
            </p>
            <Show if={Boolean(onViewReport)}>
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.XS}
                HugeIcon={LinkSquare02Icon}
                className="flex shrink-0 items-center gap-2 whitespace-nowrap !text-primary-600"
                onClick={() => onViewReport?.(applicantId, reportKey)}
              >
                View report
              </Button>
            </Show>
          </div>
          <ReviewIssueCard
            state={reportState}
            rerun={{ requested: rerunRequested, pending: rerunPending }}
            reportLabel={reportLabel}
            onRerun={() => rerun.mutate({ applicantId, reportKey })}
          />
          <ReportFindings
            findings={findings}
            loading={findingsLoading}
            failed={findingsFailed}
            state={reportState}
            rerunRequested={rerunRequested}
            renderFinding={(finding) => (
              <FindingCard
                finding={finding}
                onReview={reviewOne.mutate}
                onUndo={undoOne.mutate}
                pending={isPending(finding.id)}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-alpha-100 px-6 py-4">
        <Button
          variant={ButtonVariant.BASE}
          disabled={applicantToReviewCount === 0}
          loading={reviewAll.isPending}
          onClick={() => reviewAll.mutate(applicantId)}
        >
          Mark all as reviewed
        </Button>
        <Button variant={ButtonVariant.PRIMARY} onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
};
